import React from 'react';
import {
  CheckCircleIcon,
  PlusIcon,
  LinkIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export interface BulkActionAvailability {
  canReview: boolean;
  canCreateLeads: boolean;
  canAttach: boolean;
  canDelete: boolean;
  hasMixedStatuses: boolean;
  statusBreakdown: Record<string, number>;
}

interface BulkActionsToolbarProps {
  selectedCount: number;
  onClearSelection: () => void;
  availability: BulkActionAvailability;
  onBulkReview: () => void;
  onBulkCreateLeads: () => void;
  onBulkAttach: () => void;
  onBulkDelete: () => void;
  inProgress?: boolean;
}

const BulkActionsToolbar: React.FC<BulkActionsToolbarProps> = ({
  selectedCount,
  onClearSelection,
  availability,
  onBulkReview,
  onBulkCreateLeads,
  onBulkAttach,
  onBulkDelete,
  inProgress = false
}) => {
  if (selectedCount === 0) return null;

  const {
    canReview,
    canCreateLeads,
    canAttach,
    canDelete,
    hasMixedStatuses,
    statusBreakdown
  } = availability;

  return (
    <div className="sticky top-0 z-20 bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200 px-6 py-4 shadow-md animate-slideDown">
      <div className="flex items-center justify-between">
        {/* Left: Selection Info */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full font-bold text-sm">
              {selectedCount}
            </div>
            <span className="text-sm font-semibold text-gray-900">
              {selectedCount} {selectedCount === 1 ? 'Submission' : 'Submissions'} Selected
            </span>
          </div>
          
          <button
            onClick={onClearSelection}
            className="text-sm text-blue-700 hover:text-blue-900 font-medium flex items-center space-x-1 transition-colors"
            disabled={inProgress}
          >
            <XMarkIcon className="w-4 h-4" />
            <span>Clear Selection</span>
          </button>

          {/* Status Breakdown */}
          {hasMixedStatuses && (
            <div className="flex items-center space-x-2 text-xs">
              <span className="text-gray-500">Mixed:</span>
              {Object.entries(statusBreakdown).map(([status, count]) => (
                <span key={status} className="bg-white px-2 py-1 rounded-full text-gray-700 border border-gray-300">
                  {status}: {count}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Right: Bulk Actions */}
        <div className="flex items-center space-x-2">
          {/* Primary Actions */}
          {canReview && (
            <button
              onClick={onBulkReview}
              disabled={inProgress}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
              title="Review selected submissions"
            >
              <CheckCircleIcon className="w-4 h-4 mr-2" />
              Review ({selectedCount})
            </button>
          )}

          {canCreateLeads && (
            <button
              onClick={onBulkCreateLeads}
              disabled={inProgress}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
              title="Create leads from selected submissions"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Leads ({selectedCount})
            </button>
          )}

          {/* Secondary Actions */}
          {canAttach && (
            <div className="flex items-center space-x-2 border-l border-gray-300 pl-2">
              <button
                onClick={onBulkAttach}
                disabled={inProgress}
                className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Attach selected submissions to a lead"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Attach to Lead
              </button>
            </div>
          )}

          {/* Destructive Actions */}
          {canDelete && (
            <div className="border-l border-gray-300 pl-2">
              <button
                onClick={onBulkDelete}
                disabled={inProgress}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                title="Delete selected submissions"
              >
                <TrashIcon className="w-4 h-4 mr-2" />
                Delete ({selectedCount})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Action Hints */}
      {hasMixedStatuses && (
        <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex items-center">
          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Mixed statuses selected. Note: Only approved submissions can be converted to new leads or attached to existing leads.</span>
        </div>
      )}
      
      {/* Info for non-approved selections */}
      {!canCreateLeads && !canReview && !canAttach && selectedCount > 0 && (
        <div className="mt-3 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded px-3 py-2 flex items-center">
          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>To create leads or attach to leads, submissions must be approved first. Review and approve submissions before proceeding.</span>
        </div>
      )}
    </div>
  );
};

export default BulkActionsToolbar;
